import { IconClock, IconHistory } from "@tabler/icons-react";
import { Badge, Card, EstadoBadge, Group, Heading, PageHeader, Stack, Text, ThemeIcon, Timeline } from "@/components/ui";
import { useSolicitud } from "@/features/solicitudes/hooks/use-solicitud";
import { useHistorialSolicitud } from "../hooks/use-historial-solicitud";
import { Route } from "@/routes/_authed-tw/solicitudes/$id/historial";
import type { HistorialSolicitudItem } from "../services/historial-solicitud-service";

/**
 * Pagina de historial de cambios de una solicitud · POC v22+ de Temporal
 * Tables de SQL Server. Cada UPDATE/DELETE genera una entrada en la
 * tabla `solicitudes_de_pago_history` gestionada por SQL Server.
 *
 * UX: en lugar de mostrar TODOS los campos en cada version (ruidoso),
 * mostramos solo lo que CAMBIO respecto a la version anterior · estilo
 * audit log profesional ("Estado: X → Y").
 */
export function HistorialSolicitudPage() {
  const { id } = Route.useParams();
  const { data: sol } = useSolicitud(id);
  const { data: historial } = useHistorialSolicitud(id);

  // Para calcular diffs necesitamos las versiones en orden cronologico
  // ascendente (mas vieja primero). Despues invertimos para mostrar la
  // mas reciente arriba en la UI.
  const cronologico = [...historial].sort(
    (a, b) => new Date(a.periodStart).getTime() - new Date(b.periodStart).getTime()
  );

  // Calculamos los cambios respecto a la version anterior para cada item.
  const versionesConDiff = cronologico.map((v, idx) => ({
    version: v,
    cambios: calcularDiff(v, idx === 0 ? null : cronologico[idx - 1]),
    esVersionInicial: idx === 0,
  }));

  // Invertimos · mas reciente arriba.
  const paraMostrar = [...versionesConDiff].reverse();

  return (
    <div className="mx-auto max-w-4xl px-4 py-8 md:px-6">
      <PageHeader
        title={`Historial · Solicitud ${sol.folio}`}
        description="Cada cambio queda registrado automaticamente por SQL Server (Temporal Tables). Se muestran solo los campos que cambiaron en cada version."
        breadcrumbs={[
          { label: "Inicio", href: "/dashboard" },
          { label: "Solicitudes", href: "/solicitudes" },
          { label: sol.folio, href: `/solicitudes/${id}` },
          { label: "Historial" },
        ]}
      />

      <Stack gap="md">
        <Card padding="md">
          <Group gap="sm" wrap="nowrap" align="flex-start">
            <ThemeIcon size="lg" radius="md" variant="filled" color="multiva">
              <IconHistory size={20} stroke={1.6} />
            </ThemeIcon>
            <div>
              <Heading order={5}>
                {historial.length} versi{historial.length === 1 ? "ón" : "ones"} registrada
                {historial.length === 1 ? "" : "s"}
              </Heading>
              <Text size="sm" c="dimmed" className="mt-0.5">
                Auditoria nativa de SQL Server · imposible de evitar (incluye UPDATEs directos en BD).
              </Text>
            </div>
          </Group>
        </Card>

        <Card padding="lg">
          <Timeline active={paraMostrar.length} bulletSize={24}>
            {paraMostrar.map((entry, idx) => (
              <Timeline.Item
                key={`${entry.version.periodStart}-${idx}`}
                bullet={<IconClock size={12} />}
                title={
                  <Group gap="xs" wrap="nowrap">
                    <EstadoBadge estado={entry.version.estado} size="sm" />
                    {entry.version.esActual && <Badge size="xs" color="exito">Actual</Badge>}
                    {entry.esVersionInicial && <Badge size="xs" color="slate">Inicial</Badge>}
                  </Group>
                }
              >
                <CambiosDeVersion version={entry.version} cambios={entry.cambios} esVersionInicial={entry.esVersionInicial} />
              </Timeline.Item>
            ))}
          </Timeline>
        </Card>
      </Stack>
    </div>
  );
}

/**
 * Calcula los campos que cambiaron entre `actual` y la version `anterior`.
 * Si `anterior` es null, devuelve los valores iniciales (creacion del agregado).
 */
function calcularDiff(
  actual: HistorialSolicitudItem,
  anterior: HistorialSolicitudItem | null
): CambioCampo[] {
  const cambios: CambioCampo[] = [];

  const fmt = (v: unknown) => (v === null || v === undefined || v === "" ? "—" : String(v));

  if (anterior === null) {
    // Version inicial · solo mostramos los campos que tienen valor.
    if (actual.estado) cambios.push({ campo: "Estado", antes: null, despues: actual.estado });
    if (actual.tipo)
      cambios.push({ campo: "Tipo", antes: null, despues: `${actual.tipo}${actual.subtipo ? ` · ${actual.subtipo}` : ""}` });
    return cambios;
  }

  if (actual.estado !== anterior.estado) {
    cambios.push({ campo: "Estado", antes: anterior.estado, despues: actual.estado });
  }
  if (actual.tipo !== anterior.tipo || actual.subtipo !== anterior.subtipo) {
    cambios.push({
      campo: "Tipo / Subtipo",
      antes: `${fmt(anterior.tipo)} · ${fmt(anterior.subtipo)}`,
      despues: `${fmt(actual.tipo)} · ${fmt(actual.subtipo)}`,
    });
  }
  if (actual.organizacionId !== anterior.organizacionId) {
    cambios.push({
      campo: "Organización",
      antes: anterior.organizacionNombre
        ? `${anterior.organizacionNombre} (${fmt(anterior.organizacionNumeroSociedad)})`
        : null,
      despues: actual.organizacionNombre
        ? `${actual.organizacionNombre} (${fmt(actual.organizacionNumeroSociedad)})`
        : null,
    });
  }
  if (actual.areaNombre !== anterior.areaNombre) {
    cambios.push({ campo: "Área", antes: anterior.areaNombre, despues: actual.areaNombre });
  }
  if (
    actual.proveedorNumero !== anterior.proveedorNumero ||
    actual.proveedorRazonSocial !== anterior.proveedorRazonSocial
  ) {
    cambios.push({
      campo: "Proveedor",
      antes: anterior.proveedorRazonSocial ? `${anterior.proveedorRazonSocial} · #${anterior.proveedorNumero}` : null,
      despues: actual.proveedorRazonSocial ? `${actual.proveedorRazonSocial} · #${actual.proveedorNumero}` : null,
    });
  }
  if (actual.enviadaAContabilizacion !== anterior.enviadaAContabilizacion) {
    cambios.push({
      campo: "Contabilización",
      antes: anterior.enviadaAContabilizacion ? "Enviada" : "No enviada",
      despues: actual.enviadaAContabilizacion ? "Enviada" : "No enviada",
    });
  }

  return cambios;
}

interface CambioCampo {
  campo: string;
  antes: string | null;
  despues: string | null;
}

function CambiosDeVersion({
  version,
  cambios,
  esVersionInicial,
}: {
  version: HistorialSolicitudItem;
  cambios: CambioCampo[];
  esVersionInicial: boolean;
}) {
  const inicio = new Date(version.periodStart);
  return (
    <Stack gap="xs" className="mt-1">
      <Text size="xs" c="dimmed" tt="uppercase" fw={600} className="tracking-wide" style={{ fontSize: 10 }}>
        {inicio.toLocaleString("es-MX", {
          day: "2-digit",
          month: "long",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        })}
      </Text>

      {cambios.length === 0 ? (
        <Text size="xs" c="dimmed" className="italic">
          Cambio interno en la fila · sin diferencias visibles en los campos rastreados.
        </Text>
      ) : (
        <Stack gap={6}>
          {cambios.map((c, i) => (
            <CambioRow key={i} cambio={c} esVersionInicial={esVersionInicial} />
          ))}
        </Stack>
      )}
    </Stack>
  );
}

function CambioRow({ cambio, esVersionInicial }: { cambio: CambioCampo; esVersionInicial: boolean }) {
  // Para la version inicial mostramos solo el valor (no hay "antes").
  if (esVersionInicial) {
    return (
      <Group gap="xs" wrap="nowrap" align="baseline">
        <Text size="xs" c="dimmed" className="w-[120px] shrink-0">
          {cambio.campo}
        </Text>
        <Text size="sm">{cambio.despues ?? "—"}</Text>
      </Group>
    );
  }

  return (
    <Group gap="xs" wrap="nowrap" align="baseline">
      <Text size="xs" c="dimmed" className="w-[120px] shrink-0">
        {cambio.campo}
      </Text>
      <Group gap={6} wrap="nowrap" className="min-w-0 flex-1">
        <Text size="xs" c="dimmed" className="opacity-70 line-through">
          {cambio.antes ?? "—"}
        </Text>
        <Text size="xs" c="dimmed">→</Text>
        <Text size="sm" fw={500} c="multiva-700">
          {cambio.despues ?? "—"}
        </Text>
      </Group>
    </Group>
  );
}
